import ExcelJS from 'exceljs'

import type { NiiSheetWorkbookRows, SheetTable } from './buildNiiSheetRows'
import { NII_SHEET_LABELS } from './niiSheetSelection'

const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/**
 * Column widths are clamped: NII labels and justifications run long, and a
 * column sized to its widest cell would be unusable.
 */
const MIN_COLUMN_WIDTH = 12
const MAX_COLUMN_WIDTH = 60

/**
 * Adds one sheet, or nothing at all when the sheet has no columns — either
 * because the scope produced none or because it was excluded in the panel
 * config. An empty worksheet in the file would read as missing data.
 */
function addSheet(
  workbook: ExcelJS.Workbook,
  name: string,
  table: SheetTable
): void {
  if (table.headers.length === 0) return

  const sheet = workbook.addWorksheet(name)
  sheet.addRow([...table.headers])
  sheet.getRow(1).font = { bold: true }
  sheet.views = [{ state: 'frozen', ySplit: 1 }]
  table.rows.forEach((row) => sheet.addRow([...row]))

  sheet.columns.forEach((column, index) => {
    const longest = Math.max(
      table.headers[index]?.length ?? 0,
      ...table.rows.map((row) => row[index]?.length ?? 0)
    )
    column.width = Math.min(
      MAX_COLUMN_WIDTH,
      Math.max(MIN_COLUMN_WIDTH, longest + 2)
    )
    // Joined cells and long prose need wrapping to be readable at all.
    column.alignment = { vertical: 'top', wrapText: true }
  })
}

export function buildNiiSheetFilename(ticketCount: number): string {
  const today = new Date().toISOString().slice(0, 10)
  return `NII-Tickets-${ticketCount}-assets-${today}.xlsx`
}

/** One workbook for the whole selection, one sheet per grain. */
export async function buildNiiSheetWorkbook(
  rows: NiiSheetWorkbookRows
): Promise<Blob> {
  const workbook = new ExcelJS.Workbook()
  addSheet(workbook, NII_SHEET_LABELS.tickets, rows.tickets)
  addSheet(workbook, NII_SHEET_LABELS.zones, rows.zones)
  addSheet(workbook, NII_SHEET_LABELS.history, rows.history)
  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], { type: XLSX_MIME_TYPE })
}
